import { delay } from 'redux-saga/effects';
import { select, take, takeLatest, takeEvery, put, call } from 'redux-saga/effects';
import {
    Actions,
    getUsersSuccess, getUsersFailure,
    getUserSuccess, getUserFailure,
    deleteUserSuccess, deleteUserFailure,
    getUserRequest,
    getUsersRequest,
    GetUsersRequestParameters,
} from 'actions/userActions';
import i18n from 'i18next';
import { getUser } from 'selectors/usersSelectors';
import { confirmSaga } from 'sagas/confirmSaga';
import { apiGet, apiDelete } from 'sagas/apiSagas';
import { User } from 'shared/models';
import { getSelectedKeys } from 'logic/getSelectedKeys';
import { toError } from 'shared/util/toError';


export function* getUsersRequestWatcher() {
    yield takeLatest(Actions.GET_USERS_REQUEST, getUsersSaga);
}

type GetUsersOutput = {
    users: User[];
    more: boolean;
};

function* getUsersSaga(action: ReturnType<typeof getUsersRequest>) {
    const params = action.payload;
    const { callback, ref } = action.meta;
    const keys = Object.keys(params) as (keyof GetUsersRequestParameters)[];
    const query =
        keys
            .filter(key => params[key] && typeof params[key] !== 'function')
            .map(key =>
                (key === 'roles' || key === 'orgs')
                    ? `${key}=${(getSelectedKeys(params[key] as Record<string, boolean>)).join(',')}`
                    : `${key}=${encodeURIComponent(params[key] as string)}`
            )
            .filter(q => !!q)
            .join('&');

    try {
        // Introduce a small delay if there's a query (to throttle rapid subsequent requests)
        // (e.g. like in the scenario where a user is typing into the search text field)
        if (params.searchText) {
            yield delay(400);
        }
        const { users, more }: GetUsersOutput = yield call(apiGet, `/api/users${query ? '?' + query : ''}`);
        yield put(getUsersSuccess(users, { more, ref }));

        // The AssignUserForm uses Semantic UI dropdown with API capabilities to
        // lookup users. We force the workflow to go through our saga. Now need
        // to call the Semantic UI callback method to deliver the users.
        if (callback) {
            yield call(callback, users, more);
        }
    }
    catch (e) {
        const err = toError(e);
        yield put(getUsersFailure(err, { ref }));
        if (callback) {
            yield call(callback, [], false);
        }
    }
}


export function* getUserRequestWatcher() {
    yield takeEvery(Actions.GET_USER_REQUEST, getUserSaga);
}

function* getUserSaga(action: ReturnType<typeof getUserRequest>) {
    const userId = action.payload;
    try {
        const user: User = yield call(apiGet, `/api/users/${userId}`);
        yield put(getUserSuccess(user));
    }
    catch (e) {
        const err = toError(e);
        yield put(getUserFailure(err, { userId }));
    }
}

export function* deleteUserSaga() {
    while (true) {
        const { payload: userId } = yield take(Actions.DELETE_USER_REQUEST);

        const user: User = yield select(getUser, { userId });
        const name = user.displayName || user.login || '';
        const confirmed: boolean = yield call(confirmSaga, i18n.t('USERS.DELETE_CONFIRMATION', { name }));
        if (!confirmed) {
            continue;
        }

        try {
            yield call(apiDelete, `/api/users/${userId}`);
            yield put(deleteUserSuccess(userId));
        }
        catch (e) {
            const err = toError(e);
            yield put(deleteUserFailure(err, userId));
        }
    }
}
